import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;600;700;800&family=Noto+Serif+JP:wght@600;700&display=swap"
        rel="stylesheet"
      />

      {/* Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white border-b border-gray-200 shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: scrolled ? '#102A43' : 'rgba(255,255,255,0.2)' }}>
              <i className={`ri-time-line text-base ${scrolled ? 'text-white' : 'text-white'}`}></i>
            </div>
            <span
              className={`text-lg font-bold tracking-tight ${scrolled ? '' : 'text-white'}`}
              style={{ color: scrolled ? '#102A43' : undefined, fontFamily: "'Noto Serif JP', serif" }}
            >
              AI納期
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {[
              { label: '機能', href: '#features' },
              { label: 'こんな方に', href: '#target' },
            ].map((item) => (
              <a
                key={item.label}
                href={item.href}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                }`}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#cta"
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                backgroundColor: scrolled ? '#102A43' : 'rgba(255,255,255,0.15)',
                color: 'white',
                border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.4)',
              }}
            >
              今すぐ試す
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'} ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-3">
            <a href="#features" className="text-sm text-gray-700 py-2 cursor-pointer" onClick={() => setMenuOpen(false)}>機能</a>
            <a href="#target" className="text-sm text-gray-700 py-2 cursor-pointer" onClick={() => setMenuOpen(false)}>こんな方に</a>
            <a
              href="#cta"
              className="text-sm font-semibold text-white text-center py-2.5 rounded-lg cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: '#102A43' }}
              onClick={() => setMenuOpen(false)}
            >
              今すぐ試す
            </a>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: '#102A43' }}
      >
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/65f24a0ce8e935f275c55f9937272b51.jpg"
            alt="hero background"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #102A43 0%, #1a3a5c 50%, #102A43 100%)' }}></div>
        </div>

        <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/20 bg-white/10 text-white/80 text-xs font-medium mb-8">
            <i className="ri-sparkling-line text-yellow-300"></i>
            卸・問屋向け AI搭載 納期管理SaaS
          </div>

          {/* Main heading */}
          <h1
            className="text-3xl md:text-5xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif", letterSpacing: '-0.01em' }}
          >
            納品書・FAXを撮るだけで、<br className="hidden sm:block" />
            入荷予定が自動登録される。
          </h1>

          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-xl mx-auto">
            紙・FAX・Excelでの管理から卒業。AIが書類を読み取り、納期情報をリアルタイムで一元管理します。
          </p>

          {/* CTA */}
          <div id="cta" className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@ai-noki.com"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg text-sm font-bold text-[#102A43] bg-white hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-rocket-line"></i>
              今すぐ試す（無料）
            </a>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line"></i>
              機能を見る
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-6 text-white/50 text-xs">
            <div className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-white/40"></i>
              <span>SSL暗号化通信</span>
            </div>
            <div className="w-px h-3 bg-white/20"></div>
            <div className="flex items-center gap-1.5">
              <i className="ri-lock-line text-white/40"></i>
              <span>データ暗号化保存</span>
            </div>
            <div className="w-px h-3 bg-white/20"></div>
            <div className="flex items-center gap-1.5">
              <i className="ri-customer-service-2-line text-white/40"></i>
              <span>平日サポート対応</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/30 text-xs animate-bounce">
          <i className="ri-arrow-down-line text-lg"></i>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: '#e8eef4', color: '#102A43' }}>
              主な機能
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              3つの機能で、納期管理を<br className="hidden sm:block" />シンプルに。
            </h2>
            <p className="mt-4 text-gray-500 text-sm leading-relaxed max-w-lg mx-auto">
              複雑な設定は不要。現場の業務フローに合わせて、すぐに使い始められます。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-camera-ai-line',
                number: '01',
                title: 'AIスキャン自動登録',
                desc: '納品書・発注書・FAXをカメラで撮影するだけ。AIが文字を読み取り、商品名・数量・納期を自動で登録します。手入力ゼロを実現。',
                color: '#102A43',
                bg: '#e8eef4',
                tags: ['OCR読み取り', 'AI解析', '自動入力'],
              },
              {
                icon: 'ri-file-excel-2-line',
                number: '02',
                title: 'CSV一括インポート',
                desc: '既存のExcelデータをCSVで一括取り込み。フォーマットを自動判定し、過去データも含めてスムーズに移行できます。',
                color: '#1a5c3a',
                bg: '#e8f4ee',
                tags: ['Excel対応', '一括処理', '自動マッピング'],
              },
              {
                icon: 'ri-chat-search-line',
                number: '03',
                title: 'チャットで即検索',
                desc: '「〇〇商品の納期は？」と話しかけるだけ。AIが即座に回答。複数拠点・複数取引先の情報も一瞬で引き出せます。',
                color: '#5c3a1a',
                bg: '#f4ede8',
                tags: ['自然言語検索', 'AI回答', 'リアルタイム'],
              },
            ].map((feature) => (
              <div
                key={feature.number}
                className="border border-gray-200 rounded-xl p-6 hover:border-gray-300 transition-all group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: feature.bg }}
                  >
                    <i className={`${feature.icon} text-xl`} style={{ color: feature.color }}></i>
                  </div>
                  <span className="text-3xl font-bold text-gray-100 group-hover:text-gray-200 transition-colors" style={{ fontFamily: 'monospace' }}>
                    {feature.number}
                  </span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{feature.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {feature.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: feature.bg, color: feature.color }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Section */}
      <section id="target" className="py-24" style={{ backgroundColor: '#f7f9fb' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: '#e8eef4', color: '#102A43' }}>
              こんな方におすすめ
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              こんなお悩み、ありませんか？
            </h2>
            <p className="mt-4 text-gray-500 text-sm max-w-lg mx-auto">
              AI納期は、卸・問屋業務の現場で生まれた課題を解決するために設計されています。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
            {[
              {
                icon: 'ri-file-paper-2-line',
                problem: 'FAX・紙の発注書を手で転記している',
                solution: 'AIが自動で読み取り・登録。転記ミスゼロへ。',
              },
              {
                icon: 'ri-building-2-line',
                problem: '複数拠点で納期情報がバラバラ',
                solution: 'クラウドで一元管理。どこからでもリアルタイムに確認。',
              },
              {
                icon: 'ri-table-line',
                problem: 'Excelが複雑になりすぎて管理できない',
                solution: 'シンプルなUIで誰でも使える。Excelからの移行も簡単。',
              },
              {
                icon: 'ri-search-line',
                problem: '「あの商品いつ入荷？」に即答できない',
                solution: 'チャット検索で数秒以内に回答。顧客対応がスムーズに。',
              },
            ].map((item) => (
              <div key={item.problem} className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: '#e8eef4' }}>
                  <i className={`${item.icon} text-base`} style={{ color: '#102A43' }}></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1.5 flex items-center gap-2">
                    <i className="ri-close-circle-line text-red-400 text-base"></i>
                    {item.problem}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-check-line font-bold text-base" style={{ color: '#102A43' }}></i>
                    {item.solution}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Banner */}
          <div
            className="rounded-2xl px-8 py-10 text-center"
            style={{ backgroundColor: '#102A43' }}
          >
            <h3
              className="text-xl md:text-2xl font-bold text-white mb-3"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              まずは無料でお試しください
            </h3>
            <p className="text-white/60 text-sm mb-7">
              導入のご相談・デモのご依頼はメールにてお気軽にどうぞ。
            </p>
            <a
              href="mailto:support@ai-noki.com"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg text-sm font-bold text-[#102A43] bg-white hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              お問い合わせ・デモ依頼
            </a>
            <p className="mt-4 text-white/40 text-xs">support@ai-noki.com</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: '#102A43' }}>
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 mb-10">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/15">
                  <i className="ri-time-line text-white text-base"></i>
                </div>
                <span className="text-lg font-bold text-white" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  AI納期
                </span>
              </div>
              <p className="text-white/50 text-xs leading-relaxed max-w-xs">
                卸・問屋向けのAI搭載納期管理SaaS。<br />
                納品書・FAXを撮るだけで入荷予定が自動登録。
              </p>
            </div>

            {/* Links */}
            <div className="flex flex-col sm:flex-row gap-8">
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">サービス</p>
                <div className="flex flex-col gap-2">
                  <a href="#features" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer">機能紹介</a>
                  <a href="#target" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer">こんな方に</a>
                </div>
              </div>
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">法的情報</p>
                <div className="flex flex-col gap-2">
                  <Link to="/privacy" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer">プライバシーポリシー</Link>
                  <Link to="/terms" className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer">利用規約</Link>
                </div>
              </div>
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">お問い合わせ</p>
                <a
                  href="mailto:support@ai-noki.com"
                  className="text-white/60 text-sm hover:text-white transition-colors cursor-pointer"
                >
                  support@ai-noki.com
                </a>
                <p className="text-white/30 text-xs mt-1">平日 10:00〜18:00</p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/30 text-xs">&copy; 2025 AI納期 / 個人事業主 小助川. All rights reserved.</p>
            <div className="flex items-center gap-4 text-xs text-white/30">
              <Link to="/privacy" className="hover:text-white/60 transition-colors cursor-pointer">プライバシーポリシー</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-white/60 transition-colors cursor-pointer">利用規約</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
